import React, { useState, useEffect } from 'react';
import { doc, getDoc, updateDoc } from 'firebase/firestore';
import { db } from '../firebase';  // Adjust path as needed

export const Configurations = () => {
  const [config, setConfig] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saveStatus, setSaveStatus] = useState('');

  useEffect(() => {
    fetchConfigData();
  }, []);

  const fetchConfigData = async () => {
    try {
      const docRef = doc(db, 'configsCollection', 'eUscw2FvsZmInVFQLMQX');
      const docSnap = await getDoc(docRef);
      
      if (docSnap.exists()) {
        setConfig(docSnap.data());
      }
    } catch (error) {
      console.error("Error fetching config:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleToggleLiveMode = () => {
    setConfig(prev => ({
      ...prev,
      isLiveMode: !prev.isLiveMode
    }));
  };

  const handleURLChange = (field) => (e) => {
    setConfig(prev => ({
      ...prev,
      [field]: e.target.value
    }));
  };

  const handleSave = async () => {
    try {
      setLoading(true);
      setSaveStatus('saving');
      const docRef = doc(db, 'configsCollection', 'eUscw2FvsZmInVFQLMQX');
      await updateDoc(docRef, {
        isLiveMode: config.isLiveMode,
        liveURL: config.liveURL,
        testURL: config.testURL
      });
      setSaveStatus('success');
      setIsEditing(false);
    } catch (error) {
      console.error("Error updating config:", error);
      setSaveStatus('error');
    } finally {
      setLoading(false);
      setTimeout(() => setSaveStatus(''), 3000);
    }
  };

  if (loading && !config) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="w-8 h-8 border-b-2 border-blue-500 rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="max-w-3xl p-6 mx-auto">
      <div className="p-6 bg-white rounded-lg shadow-lg dark:bg-gray-800">
        <div className="flex items-center justify-between mb-6">
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Configurations</h1>
          <span className={`px-3 py-1 rounded-full text-sm font-medium ${
            config?.isLiveMode 
              ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
              : 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200'
          }`}>
            {config?.isLiveMode ? 'Live Mode' : 'Test Mode'}
          </span>
        </div>

        <div className="space-y-6">
          <div className="flex items-center justify-between">
            <span className="font-medium text-gray-700 dark:text-gray-200">Mode Toggle</span>
            <button
              onClick={handleToggleLiveMode}
              disabled={!isEditing}
              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 ${
                config?.isLiveMode ? 'bg-headings' : 'bg-gray-200'
              } ${!isEditing ? 'opacity-50 cursor-not-allowed' : ''}`}
            >
              <span
                className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                  config?.isLiveMode ? 'translate-x-6' : 'translate-x-1'
                }`}
              />
            </button>
          </div>

          <div className="space-y-4">
            <div>
              <label className="block mb-2 text-sm font-medium text-gray-700 dark:text-gray-200">
                Live URL
              </label>
              <div className="flex space-x-2">
                <input
                  type="text"
                  value={config?.liveURL || ''}
                  onChange={handleURLChange('liveURL')}
                  disabled={!isEditing}
                  className={`flex-1 rounded-md border border-gray-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                    !isEditing ? 'bg-gray-50 text-gray-500' : 'bg-white'
                  }`}
                  placeholder="Enter live URL"
                />
                <a
                  href={config?.liveURL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                >
                  Visit
                </a>
              </div>
            </div>

            <div>
              <label className="block mb-2 text-sm font-medium text-gray-700 dark:text-gray-200">
                Test URL
              </label>
              <div className="flex space-x-2">
                <input
                  type="text"
                  value={config?.testURL || ''}
                  onChange={handleURLChange('testURL')}
                  disabled={!isEditing}
                  className={`flex-1 rounded-md border border-gray-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                    !isEditing ? 'bg-gray-50 text-gray-500' : 'bg-white'
                  }`}
                  placeholder="Enter test URL"
                />
                <a
                  href={config?.testURL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                >
                  Visit
                </a>
              </div>
            </div>
          </div>

          <div className="flex justify-end pt-4 space-x-3">
            {!isEditing ? (
              <button
                onClick={() => setIsEditing(true)}
                className="inline-flex items-center px-4 py-2 text-sm font-medium text-white border border-transparent rounded-md shadow-sm bg-headings hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
              >
                Edit Configuration
              </button>
            ) : (
              <>
                <button
                  onClick={() => setIsEditing(false)}
                  className="inline-flex items-center px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSave}
                  disabled={loading}
                  className="inline-flex items-center px-4 py-2 text-sm font-medium text-white border border-transparent rounded-md shadow-sm bg-headings hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {loading ? (
                    <span className="inline-flex items-center">
                      <svg className="w-4 h-4 mr-2 -ml-1 text-white animate-spin" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"/>
                      </svg>
                      Saving...
                    </span>
                  ) : 'Save Changes'}
                </button>
              </>
            )}
          </div>

          {saveStatus && (
            <div className={`mt-2 text-sm ${
              saveStatus === 'success' ? 'text-green-600' : 
              saveStatus === 'error' ? 'text-red-600' : 
              'text-gray-600'
            }`}>
              {saveStatus === 'success' ? 'Changes saved successfully!' :
               saveStatus === 'error' ? 'Error saving changes. Please try again.' :
               'Saving changes...'}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// export default Configurations;